/**
 * The pickup code shown as a QR in the app (CLAUDE.md §4). It refreshes every
 * 30 seconds and works offline: the phone holds a per-customer secret and
 * signs the current 30-second step with HMAC-SHA256; the till verifies the
 * same way. A code is accepted for one step either side (60–90 s of drift).
 *
 * Token text: "TK1.<customerId>.<step>.<signature>" (scanner types this as keyboard input).
 */
export const QR_STEP_SECONDS = 30;
export const QR_PREFIX = 'TK1';

function b64url(bytes: ArrayBuffer): string {
  let s = '';
  for (const b of new Uint8Array(bytes)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function hmac(secretHex: string, message: string): Promise<string> {
  const keyBytes = new Uint8Array(secretHex.match(/.{1,2}/g)!.map((h) => parseInt(h, 16)));
  const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(message));
  return b64url(sig).slice(0, 16);
}

export function qrStep(now: Date = new Date()): number {
  return Math.floor(now.getTime() / 1000 / QR_STEP_SECONDS);
}

/** Seconds until the current code changes. */
export function qrSecondsLeft(now: Date = new Date()): number {
  return QR_STEP_SECONDS - (Math.floor(now.getTime() / 1000) % QR_STEP_SECONDS);
}

export async function pickupToken(secretHex: string, customerId: string, now: Date = new Date()): Promise<string> {
  const step = qrStep(now);
  const sig = await hmac(secretHex, `${QR_PREFIX}.${customerId}.${step}`);
  return `${QR_PREFIX}.${customerId}.${step}.${sig}`;
}

export function parsePickupToken(token: string): { customerId: string; step: number; sig: string } | null {
  const parts = token.trim().split('.');
  if (parts.length !== 4 || parts[0] !== QR_PREFIX) return null;
  const step = Number(parts[2]);
  if (!parts[1] || !Number.isInteger(step) || !parts[3]) return null;
  return { customerId: parts[1], step, sig: parts[3] };
}

/** Verify a scanned token against the customer's secret. `tolerance` = steps either side. */
export async function verifyPickupToken(secretHex: string, token: string, now: Date = new Date(), tolerance = 1): Promise<boolean> {
  const p = parsePickupToken(token);
  if (!p) return false;
  const current = qrStep(now);
  if (Math.abs(p.step - current) > tolerance) return false;
  const expected = await hmac(secretHex, `${QR_PREFIX}.${p.customerId}.${p.step}`);
  if (expected.length !== p.sig.length) return false;
  let diff = 0;
  for (let i = 0; i < expected.length; i++) diff |= expected.charCodeAt(i) ^ p.sig.charCodeAt(i);
  return diff === 0;
}
